// frontend/src/components/Dashboard.js
import React, { useState, useEffect } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { Warehouse, TrendingUp, Package, AlertTriangle, Settings } from 'lucide-react';
import { stockAPI, productionAPI, salesAPI } from '../services/api';
import { useWebSocket } from '../hooks/useWebSocket';
import ProductionForm from './ProductionForm';
import ProductionOverview from './ProductionOverview';
import StockOverview from './StockOverview';
import SalesForm from './SalesForm';
import SalesOverview from './SalesOverview';
import AdminPanel from './AdminPanel';
import toast from 'react-hot-toast';

function Dashboard({ user, onLogout }) {
  const [activeTab, setActiveTab] = useState('overview');
  const [productionSubTab, setProductionSubTab] = useState('register');
  const [salesSubTab, setSalesSubTab] = useState('register');
  const { connected } = useWebSocket();
  const queryClient = useQueryClient();

  // Queries
  const { data: stockData, isLoading: stockLoading } = useQuery(
    'stock',
    stockAPI.getCurrent,
    { 
      refetchInterval: connected ? false : 30000, // Poll hver 30 sek hvis ikke WebSocket
      select: data => data.data 
    }
  );

  const { data: productionOverview } = useQuery(
    'production-overview',
    productionAPI.getStatusOverview,
    { 
      refetchInterval: connected ? false : 60000,
      select: data => data.data 
    }
  );

  const { data: lowStock } = useQuery(
    'low-stock',
    () => stockAPI.getLowStock(10),
    { 
      refetchInterval: 60000,
      select: data => data.data 
    }
  );

  // Listen for WebSocket updates
  useEffect(() => {
    const handleRefetch = (event) => {
      queryClient.invalidateQueries();
    };

    window.addEventListener('refetchData', handleRefetch);
    return () => window.removeEventListener('refetchData', handleRefetch);
  }, [queryClient]);

  const totalStock = stockData?.reduce((sum, item) => sum + item.totalStock, 0) || 0;
  const totalProduced = stockData?.reduce((sum, item) => sum + item.totalProduced, 0) || 0;
  const totalSold = stockData?.reduce((sum, item) => sum + item.totalSold, 0) || 0;

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <h1 className="text-2xl font-bold text-gray-900">Lagermodul</h1>
            <div className="flex items-center space-x-4">
              <div className={`px-2 py-1 rounded-full text-xs font-medium ${
                connected ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
              }`}>
                {connected ? 'Live' : 'Offline'}
              </div>
              {user && (
                <div className="flex items-center space-x-3 border-l pl-4">
                  <div className="text-right">
                    <p className="text-sm font-medium text-gray-900">{user.name}</p>
                    <p className="text-xs text-gray-500">{user.email}</p>
                  </div>
                  <button
                    onClick={onLogout}
                    className="px-3 py-1 text-sm font-medium text-red-600 hover:text-red-800 hover:bg-red-50 rounded transition-colors"
                  >
                    Log ud
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Navigation */}
      <nav className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex space-x-8">
            {[
              { id: 'overview', label: 'Oversigt', icon: Warehouse },
              { id: 'production', label: 'Produktion', icon: Package },
              { id: 'sales', label: 'Salg', icon: TrendingUp },
              { id: 'stock', label: 'Lager', icon: AlertTriangle },
              { id: 'admin', label: 'Administration', icon: Settings }
            ].map(tab => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center space-x-2 py-4 px-2 border-b-2 transition-colors ${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <Icon size={18} />
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      </nav>

      {/* Production Sub-Navigation */}
      {activeTab === 'production' && (
        <div className="bg-gray-50 border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex space-x-6">
              {[
                { id: 'register', label: 'Registrer Støbning' },
                { id: 'overview', label: 'Produktions Oversigt' }
              ].map(subTab => (
                <button
                  key={subTab.id}
                  onClick={() => setProductionSubTab(subTab.id)}
                  className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
                    productionSubTab === subTab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {subTab.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Sales Sub-Navigation */}
      {activeTab === 'sales' && (
        <div className="bg-gray-50 border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex space-x-6">
              {[
                { id: 'register', label: 'Registrer Salg' },
                { id: 'overview', label: 'Salgs Oversigt' }
              ].map(subTab => (
                <button
                  key={subTab.id}
                  onClick={() => setSalesSubTab(subTab.id)}
                  className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
                    salesSubTab === subTab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {subTab.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* KPI Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center">
                  <Package className="h-8 w-8 text-blue-600" />
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Total Lager</p>
                    <p className="text-2xl font-semibold text-gray-900">{totalStock}</p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center">
                  <TrendingUp className="h-8 w-8 text-green-600" />
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Total Produceret</p>
                    <p className="text-2xl font-semibold text-gray-900">{totalProduced}</p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center">
                  <Warehouse className="h-8 w-8 text-purple-600" />
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Total Solgt</p>
                    <p className="text-2xl font-semibold text-gray-900">{totalSold}</p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center">
                  <AlertTriangle className="h-8 w-8 text-red-600" />
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Lavt Lager</p>
                    <p className="text-2xl font-semibold text-gray-900">{lowStock?.length || 0}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Production Status */}
            <div className="bg-white rounded-lg shadow">
              <div className="p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Produktionsstatus</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {productionOverview?.map(status => (
                    <div key={status.status} className="text-center p-4 bg-gray-50 rounded-lg">
                      <p className="text-2xl font-bold text-gray-900">{status.count}</p>
                      <p className="text-sm text-gray-600 capitalize">
                        {status.status.toLowerCase().replace('_', ' ')}
                      </p>
                      <p className="text-xs text-gray-500">
                        {status.totalQuantity} enheder
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'production' && (
          <>
            {productionSubTab === 'register' && <ProductionForm />}
            {productionSubTab === 'overview' && <ProductionOverview />}
          </>
        )}

        {activeTab === 'sales' && (
          <>
            {salesSubTab === 'register' && <SalesForm />}
            {salesSubTab === 'overview' && <SalesOverview />}
          </>
        )}

        {activeTab === 'stock' && <StockOverview data={stockData} loading={stockLoading} />}
        {activeTab === 'admin' && <AdminPanel />}
      </main>
    </div>
  );
}

export default Dashboard;